/**
 * The single source of truth for an assistant turn's body.
 *
 * Consolidates the previously-overlapping "working" signals (activity card +
 * streaming text + typing indicator) into one surface:
 *
 *  1. No text + no tools  → one spinner + a shimmering "Thinking…".
 *  2. No text + tools run → a vertical timeline rail; only the active node animates.
 *  3. Text present         → a collapsed "✓ Ran N tools · Xs" summary (expands to
 *     the rail) above the markdown answer, plus a blinking cursor while streaming.
 *
 * Each rail step expands to reuse the activity-timeline DetailField (input args,
 * output, error + copy), so the calm rail keeps full debuggability on demand.
 *
 * Used for both the live stream and persisted turns (mapped from
 * metadata.toolSteps) so resolved messages match the live ones.
 */

import { useState } from "react";
import { Loader2, Check, X, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { MarkdownRenderer } from "@/components/chat/markdown-renderer";
import {
  type ActivityEvent,
  getToolIcon,
  formatToolName,
  formatDuration,
  DetailField,
} from "@/components/chat/activity-timeline";

type TurnStatus = "active" | "done" | "failed";

function normStatus(status: ActivityEvent["status"]): TurnStatus {
  if (status === "started") return "active";
  if (status === "failed") return "failed";
  return "done";
}

export function AgentTurn({
  events,
  content,
  streaming,
}: {
  events: ActivityEvent[];
  content: string;
  streaming: boolean;
}) {
  const hasContent = content.trim().length > 0;
  const hasEvents = events.length > 0;

  // 1. Nothing has arrived yet — a single spinner, no dots, no card.
  if (!hasContent && !hasEvents) {
    return <ThinkingLine />;
  }

  // 2. Tools are running but no text yet — show the live rail.
  if (!hasContent) {
    return <TimelineRail events={events} />;
  }

  // 3. Text is streaming or done — collapse tools to a one-line summary.
  return (
    <div>
      {hasEvents && <TurnSummary events={events} />}
      <MarkdownRenderer content={content} streaming={streaming} />
      {streaming && <span className="streaming-cursor ml-0.5 align-baseline text-accent">▍</span>}
    </div>
  );
}

function ThinkingLine() {
  return (
    <div className="flex items-center gap-2 text-sm">
      <Loader2 size={14} className="shrink-0 animate-spin text-accent" />
      <span className="agent-shimmer font-medium">Thinking…</span>
    </div>
  );
}

/** Collapsed, clickable summary that expands to the full rail. */
function TurnSummary({ events }: { events: ActivityEvent[] }) {
  const [open, setOpen] = useState(false);
  const total = events.reduce((sum, e) => sum + (e.durationMs ?? 0), 0);
  const failed = events.some((e) => normStatus(e.status) === "failed");
  const n = events.length;
  const durationLabel = total > 0 ? ` · ${formatDuration(total)}` : "";

  return (
    <div className="mb-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 text-xs text-text-muted transition-colors hover:text-text-secondary"
      >
        {failed ? (
          <X size={12} className="shrink-0 text-danger" />
        ) : (
          <Check size={12} className="shrink-0 text-accent" />
        )}
        <span>
          Ran {n} tool{n === 1 ? "" : "s"}
          {durationLabel}
        </span>
        <ChevronDown size={12} className={cn("shrink-0 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="mt-2">
          <TimelineRail events={events} />
        </div>
      )}
    </div>
  );
}

/** Vertical timeline rail — one node per tool, only the active one animates. */
function TimelineRail({ events }: { events: ActivityEvent[] }) {
  return (
    <div className="flex flex-col">
      {events.map((event, i) => {
        const status = normStatus(event.status);
        const next = events[i + 1];
        const nextActive = next ? normStatus(next.status) === "active" : false;
        return (
          <RailRow
            key={event.toolUseId}
            event={event}
            status={status}
            isLast={i === events.length - 1}
            nextActive={nextActive}
          />
        );
      })}
    </div>
  );
}

function RailRow({
  event,
  status,
  isLast,
  nextActive,
}: {
  event: ActivityEvent;
  status: TurnStatus;
  isLast: boolean;
  nextActive: boolean;
}) {
  const [open, setOpen] = useState(false);
  const Icon = getToolIcon(event.name);
  const name = formatToolName(event.name);
  const duration = formatDuration(event.durationMs);
  const hasDetail = !!(event.args || event.resultPreview || event.error);
  const subline =
    status === "failed" && event.error
      ? event.error
      : `${name}${duration ? ` · ${duration}` : ""}`;

  const label = (
    <>
      {status === "active" && <Icon size={12} className="shrink-0 text-accent" />}
      <span className="truncate capitalize">{name}</span>
    </>
  );

  return (
    <div className="flex gap-2.5">
      {/* Node + connector column */}
      <div className="flex w-[18px] flex-col items-center">
        <Node status={status} />
        {!isLast && (
          <div
            className="min-h-[14px] w-0.5 flex-1"
            style={{
              background: nextActive
                ? "linear-gradient(var(--accent), var(--border-color))"
                : "var(--border-color)",
            }}
          />
        )}
      </div>

      {/* Content */}
      <div className={cn("min-w-0 flex-1", isLast ? "pb-0.5" : "pb-3")}>
        {hasDetail ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className={cn(
              "flex w-full items-center gap-1.5 text-left text-[13px] leading-tight",
              status === "active" ? "font-medium text-text-primary" : "text-text-secondary",
            )}
          >
            {label}
            <ChevronDown
              size={11}
              className={cn("ml-auto shrink-0 text-text-muted transition-transform", open && "rotate-180")}
            />
          </button>
        ) : (
          <div
            className={cn(
              "flex items-center gap-1.5 text-[13px] leading-tight",
              status === "active" ? "font-medium text-text-primary" : "text-text-secondary",
            )}
          >
            {label}
          </div>
        )}
        <div
          className={cn(
            "mt-0.5 truncate font-mono text-[11px]",
            status === "failed" ? "text-danger" : "text-text-muted",
          )}
          title={subline}
        >
          {subline}
        </div>
        {open && hasDetail && (
          <div className="mt-1">
            {event.args && <DetailField label="Input" value={event.args} />}
            {event.resultPreview && <DetailField label="Output" value={event.resultPreview} />}
            {event.error && <DetailField label="Error" value={event.error} tone="error" />}
          </div>
        )}
      </div>
    </div>
  );
}

function Node({ status }: { status: TurnStatus }) {
  if (status === "done") {
    return (
      <div className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent">
        <Check size={9} strokeWidth={3} />
      </div>
    );
  }
  if (status === "failed") {
    return (
      <div className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-danger/15 text-danger">
        <X size={9} strokeWidth={3} />
      </div>
    );
  }
  // Active — pulsing accent ring (see .agent-node-active in globals.css).
  return (
    <div className="agent-node-active flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-background">
      <span className="h-[5px] w-[5px] rounded-full bg-accent" />
    </div>
  );
}
